/**
 * @crm/shared — wspólne typy DTO, schematy zod, enumy ról.
 *
 * Enumy: SQLite + Prisma 5 nie wspiera typu `enum`, więc w bazie trzymamy
 * je jako String. Tutaj jest jedyny punkt prawdy dozwolonych wartości
 * — używany do walidacji w API (zod) i typowania w UI.
 */
import { z } from 'zod';
export const SHARED_PACKAGE_VERSION = '0.1.0';
// ---------------------------------------------------------------------
// Enumy domenowe (string-literal unions + zod)
// ---------------------------------------------------------------------
export const RoleSchema = z.enum(['ADMIN', 'RECEPTION', 'EMPLOYEE']);
export const ROLES = RoleSchema.options;
export const AppointmentStatusSchema = z.enum([
    'BOOKED',
    'CONFIRMED',
    'COMPLETED',
    'CANCELLED',
    'NO_SHOW',
]);
export const AppointmentSourceSchema = z.enum(['PUBLIC', 'STAFF']);
export const NoteVisibilitySchema = z.enum(['EMPLOYEE', 'TEAM']);
export const ExceptionTypeSchema = z.enum([
    'VACATION',
    'SICK_LEAVE',
    'TIME_OFF',
    'REPLACE',
    'EXTRA_DAY',
]);
export const NotificationChannelSchema = z.enum(['SMS', 'EMAIL', 'IN_APP']);
export const NotificationTypeSchema = z.enum([
    'BOOKING_CONFIRMATION',
    'REMINDER_24H',
    'CANCELLATION',
    'RESCHEDULED',
    'STAFF_NEW_BOOKING',
    'STAFF_CANCELLATION',
    'STAFF_RESCHEDULED',
]);
export const NotificationStatusSchema = z.enum(['PENDING', 'SENT', 'FAILED']);
// ---------------------------------------------------------------------
// Auth (Faza 2)
// ---------------------------------------------------------------------
export const LoginRequestSchema = z.object({
    email: z.string().trim().toLowerCase().email('Nieprawidłowy adres e-mail.'),
    password: z.string().min(1, 'Hasło jest wymagane.'),
});
export const AuthUserSchema = z.object({
    id: z.number().int().positive(),
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    role: RoleSchema,
    active: z.boolean(),
    bookable: z.boolean(),
    color: z.string().nullable(),
});
// ---------------------------------------------------------------------
// Wspólne walidatory pól
// ---------------------------------------------------------------------
export const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;
const HEX_COLOR_MSG = 'Kolor musi być w formacie #RRGGBB.';
export const PASSWORD_MIN_LENGTH = 8;
const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Hasło musi mieć co najmniej ${PASSWORD_MIN_LENGTH} znaków.`)
    .max(72, 'Hasło może mieć najwyżej 72 znaki.'); // bcrypt limit
const colorSchema = z
    .string()
    .regex(HEX_COLOR_REGEX, HEX_COLOR_MSG)
    .nullable()
    .optional();
const trimmedNonEmpty = (label, max = 100) => z.string().trim().min(1, `${label} jest wymagane.`).max(max);
// ---------------------------------------------------------------------
// Faza 4 — Users (panel pracowniczy)
// ---------------------------------------------------------------------
/** Filtr listy użytkowników (admin). */
export const UserListQuerySchema = z.object({
    active: z
        .enum(['true', 'false', 'all'])
        .optional()
        .default('true'),
    role: RoleSchema.optional(),
    search: z.string().trim().max(100).optional(),
});
export const UserCreateSchema = z.object({
    email: z.string().trim().toLowerCase().email('Nieprawidłowy adres e-mail.'),
    password: passwordSchema,
    firstName: trimmedNonEmpty('Imię', 50),
    lastName: trimmedNonEmpty('Nazwisko', 80),
    role: RoleSchema,
    bookable: z.boolean().optional().default(false),
    color: colorSchema,
    bio: z.string().trim().max(500).nullable().optional(),
});
export const UserUpdateSchema = z
    .object({
    email: z.string().trim().toLowerCase().email('Nieprawidłowy adres e-mail.').optional(),
    firstName: trimmedNonEmpty('Imię', 50).optional(),
    lastName: trimmedNonEmpty('Nazwisko', 80).optional(),
    role: RoleSchema.optional(),
    active: z.boolean().optional(),
    bookable: z.boolean().optional(),
    color: colorSchema,
    bio: z.string().trim().max(500).nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Brak pól do zaktualizowania.' });
export const PasswordResetSchema = z.object({
    password: passwordSchema,
});
/** PUT /users/:id/services — pełna podmiana listy specjalizacji. */
export const UserServicesUpdateSchema = z.object({
    serviceIds: z.array(z.number().int().positive()).max(200),
});
export const UserDirectoryQuerySchema = z.object({
    bookable: z.enum(['true', 'false']).optional(),
});
// ---------------------------------------------------------------------
// Faza 4 — Services (katalog usług)
// ---------------------------------------------------------------------
const priceSchema = z
    .union([z.string(), z.number()])
    .transform((v) => (typeof v === 'number' ? v.toFixed(2) : v.trim()))
    .pipe(z
    .string()
    .regex(/^\d{1,8}(\.\d{1,2})?$/, 'Cena musi być liczbą (np. 120.00).')
    .refine((v) => Number(v) >= 0, 'Cena nie może być ujemna.'));
export const ServiceCreateSchema = z.object({
    name: trimmedNonEmpty('Nazwa', 120),
    description: z.string().trim().max(1000).nullable().optional(),
    durationMinutes: z
        .number()
        .int('Czas trwania musi być pełnymi minutami.')
        .min(5, 'Minimalny czas trwania to 5 minut.')
        .max(8 * 60, 'Maksymalny czas trwania to 8 godzin.'),
    price: priceSchema,
    category: z.string().trim().max(60).nullable().optional(),
});
export const ServiceUpdateSchema = z
    .object({
    name: trimmedNonEmpty('Nazwa', 120).optional(),
    description: z.string().trim().max(1000).nullable().optional(),
    durationMinutes: z
        .number()
        .int()
        .min(5)
        .max(8 * 60)
        .optional(),
    price: priceSchema.optional(),
    category: z.string().trim().max(60).nullable().optional(),
    active: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Brak pól do zaktualizowania.' });
export const ServiceListQuerySchema = z.object({
    active: z.enum(['true', 'false', 'all']).optional().default('true'),
    category: z.string().trim().max(60).optional(),
    search: z.string().trim().max(100).optional(),
});
// ---------------------------------------------------------------------
// Faza 5 — Clients (klienci salonu, bez konta)
// ---------------------------------------------------------------------
/**
 * Numer telefonu — wymagany. Akceptujemy dowolne znaki na wejściu (cyfry,
 * spacje, myślniki, +, nawiasy); normalizację do E.164 robi backend
 * przez libphonenumber-js (domyślny region: PL).
 *
 * Tu tylko podstawowa walidacja długości — żeby nie puszczać pustych
 * stringów ani ekstremalnie długich payloadów. Rzeczywista poprawność
 * weryfikowana po normalizacji.
 */
const phoneInputSchema = z
    .string()
    .trim()
    .min(6, 'Numer telefonu jest wymagany.')
    .max(32, 'Numer telefonu jest za długi.');
const emailOptionalSchema = z
    .union([z.literal(''), z.string().trim().toLowerCase().email('Nieprawidłowy adres e-mail.')])
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
export const ClientCreateSchema = z.object({
    firstName: trimmedNonEmpty('Imię', 50),
    lastName: z.string().trim().max(80).nullable().optional(),
    phone: phoneInputSchema,
    email: emailOptionalSchema,
    marketingConsent: z.boolean().optional().default(false),
    rodoConsent: z.boolean().optional().default(false),
});
export const ClientUpdateSchema = z
    .object({
    firstName: trimmedNonEmpty('Imię', 50).optional(),
    lastName: z.string().trim().max(80).nullable().optional(),
    phone: phoneInputSchema.optional(),
    email: emailOptionalSchema,
    marketingConsent: z.boolean().optional(),
    rodoConsent: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Brak pól do zaktualizowania.' });
export const ClientListQuerySchema = z.object({
    search: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().min(1).max(10000).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    includeAnonymized: z.enum(['true', 'false']).optional().default('false'),
});
/**
 * Normalizuje tekst do porównań wyszukiwarki (case-insensitive,
 * bez polskich diakrytyków). NFD rozkłada większość znaków
 * (ą/ć/ę/ń/ó/ś/ź/ż → litera + combining mark, marka usuwana regexem),
 * ale `Ł/ł` to osobny code point bez dekompozycji — podmieniamy ręcznie.
 *
 * Stosowane:
 *   - przy budowie kolumny `Client.searchKey` (po stronie BE),
 *   - przy normalizacji query użytkownika przed `contains`.
 *
 * Przykłady:
 *   "Wójcik"  → "wojcik"
 *   "Łukasz"  → "lukasz"
 *   "Żarówa"  → "zarowa"
 */
const COMBINING_MARKS = new RegExp('[\\u0300-\\u036f]', 'g');
export function normalizeSearch(text) {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(COMBINING_MARKS, '')
        .replace(/ł/g, 'l');
}
// ---------------------------------------------------------------------
// Faza 6 — Grafiki pracy + wyjątki
// ---------------------------------------------------------------------
/**
 * Format godziny "HH:MM" w 24h. Wymuszone zero-padding (np. "09:00", "17:30")
 * — dzięki temu porównania jako stringi działają jak porównania czasowe.
 */
export const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;
const TIME_MSG = 'Godzina musi być w formacie HH:MM (np. 09:00).';
const timeStringSchema = z.string().regex(TIME_REGEX, TIME_MSG);
/** Dni tygodnia: 0=niedziela, 1=poniedziałek, …, 6=sobota (zgodnie z `Date.getDay()`). */
export const WeekdaySchema = z.number().int().min(0).max(6);
/**
 * Pojedynczy blok pracy w danym dniu tygodnia. Pracownik może mieć w jednym
 * dniu N bloków (np. 09:00–13:00 i 15:00–19:00 z przerwą obiadową).
 *
 * Walidacja `endTime > startTime` jest po stronie schematu, kolizja bloków
 * w obrębie dnia — w warstwie service'u (zna kontekst całego tygodnia).
 */
export const ScheduleBlockSchema = z
    .object({
    weekday: WeekdaySchema,
    startTime: timeStringSchema,
    endTime: timeStringSchema,
})
    .refine((b) => b.endTime > b.startTime, {
    message: 'Koniec pracy musi być po jej początku.',
    path: ['endTime'],
});
/** PUT /schedules/:userId — zastępuje pełen tygodniowy grafik (MVP, jeden aktywny). */
export const WeeklySchedulePutSchema = z.object({
    blocks: z.array(ScheduleBlockSchema).max(7 * 8, 'Za dużo bloków pracy.'),
});
/**
 * Wyjątek w grafiku — urlop / L4 / wolne / inne godziny / dodatkowy dzień.
 * - VACATION / SICK_LEAVE / TIME_OFF: pracownik niedostępny w `date`.
 *   `startTime`/`endTime` ignorowane.
 * - REPLACE: dla `date` zastępujemy okno pracy pochodzące ze stałego grafiku
 *   (`startTime`/`endTime` wymagane).
 * - EXTRA_DAY: dodajemy dzień pracy, którego nie ma w stałym grafiku
 *   (`startTime`/`endTime` wymagane).
 */
export const ScheduleExceptionCreateSchema = z
    .object({
    userId: z.number().int().positive(),
    date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data musi być w formacie YYYY-MM-DD.'),
    type: ExceptionTypeSchema,
    startTime: timeStringSchema.optional(),
    endTime: timeStringSchema.optional(),
    reason: z.string().trim().max(500).nullable().optional(),
})
    .superRefine((v, ctx) => {
    const needsTime = v.type === 'REPLACE' || v.type === 'EXTRA_DAY';
    if (needsTime) {
        if (!v.startTime) {
            ctx.addIssue({
                code: 'custom',
                message: 'Dla REPLACE / EXTRA_DAY wymagana jest godzina rozpoczęcia.',
                path: ['startTime'],
            });
        }
        if (!v.endTime) {
            ctx.addIssue({
                code: 'custom',
                message: 'Dla REPLACE / EXTRA_DAY wymagana jest godzina zakończenia.',
                path: ['endTime'],
            });
        }
        if (v.startTime && v.endTime && v.endTime <= v.startTime) {
            ctx.addIssue({
                code: 'custom',
                message: 'Koniec pracy musi być po jej początku.',
                path: ['endTime'],
            });
        }
    }
});
export const ScheduleExceptionListQuerySchema = z.object({
    userId: z.coerce.number().int().positive().optional(),
    from: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data musi być w formacie YYYY-MM-DD.')
        .optional(),
    to: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data musi być w formacie YYYY-MM-DD.')
        .optional(),
});
// ---------------------------------------------------------------------
// Faza 7 — Wizyty (wewnętrzny kalendarz)
// ---------------------------------------------------------------------
/**
 * ISO 8601 datetime z opcjonalną strefą. Frontend wysyła pełen ISO
 * (np. "2026-05-12T10:30:00+02:00" lub "...Z"). Zod tylko sprawdza,
 * że Date.parse zwraca poprawny moment.
 */
const isoDateTimeSchema = z
    .string()
    .min(10)
    .refine((v) => !Number.isNaN(Date.parse(v)), {
    message: 'Nieprawidłowy format daty/czasu (oczekiwane ISO 8601).',
});
const isoDateOnlySchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data musi być w formacie YYYY-MM-DD.');
/**
 * Pojedyncza usługa w wizycie (wejście). `order` opcjonalny — jeśli
 * pominięte, backend nadaje kolejność wg pozycji w tablicy.
 */
export const AppointmentServiceInputSchema = z.object({
    serviceId: z.number().int().positive(),
    order: z.number().int().min(0).max(50).optional(),
});
/**
 * POST /appointments — utworzenie wizyty z panelu (source=STAFF).
 * `force=true` pozwala zignorować ostrzeżenia walidacji biznesowej
 * (poza grafikiem / kolizja / urlop / pracownik bez specjalizacji).
 * Walidacja techniczna (istnienie zasobów, format daty) zawsze obowiązuje.
 */
export const AppointmentCreateSchema = z.object({
    clientId: z.number().int().positive(),
    employeeId: z.number().int().positive(),
    startAt: isoDateTimeSchema,
    services: z.array(AppointmentServiceInputSchema).min(1, 'Wizyta wymaga co najmniej jednej usługi.').max(20),
    status: AppointmentStatusSchema.optional(),
    force: z.boolean().optional(),
});
/**
 * PATCH /appointments/:id — zmiana godziny / pracownika / usług / statusu.
 * Wszystkie pola opcjonalne; pusty obiekt odrzucony. Status COMPLETED /
 * NO_SHOW / CANCELLED przepuszczamy do dedykowanych endpointów (RBAC i
 * dodatkowy stan typu cancelledAt). Tutaj dozwolone tylko BOOKED/CONFIRMED.
 */
export const AppointmentUpdateSchema = z
    .object({
    employeeId: z.number().int().positive().optional(),
    startAt: isoDateTimeSchema.optional(),
    services: z.array(AppointmentServiceInputSchema).min(1).max(20).optional(),
    status: z.enum(['BOOKED', 'CONFIRMED']).optional(),
    force: z.boolean().optional(),
})
    .refine((v) => v.employeeId !== undefined ||
    v.startAt !== undefined ||
    v.services !== undefined ||
    v.status !== undefined, { message: 'Brak pól do zaktualizowania.' });
/** POST /appointments/:id/cancel */
export const AppointmentCancelSchema = z.object({
    reason: z.string().trim().max(500).nullable().optional(),
});
/**
 * POST /appointments/:id/complete — Faza 19.
 *
 * `force=true` pozwala ADMIN-owi obejść próg OVERDUE_COMPLETION_DAYS
 * (domyślnie 30 dni od `startAt`). Inne role nigdy nie mogą obejść —
 * `force` ignorowane dla nie-ADMIN-a (backend i tak zwróci 409, ale
 * walidacja jest defensywna: ostatecznie decyduje backend).
 *
 * Body może być w ogóle pominięte (`POST /complete` bez payloadu) — wtedy
 * jak dotąd, dla wizyt <30 dni działa identycznie.
 */
export const AppointmentCompleteSchema = z
    .object({
    force: z.boolean().optional(),
})
    .optional()
    .default({});
/**
 * GET /appointments/overdue — lista wizyt BOOKED/CONFIRMED, których
 * `startAt < now`, czyli wizyt do zatwierdzenia (pracownik zapomniał kliknąć
 * „Zakończ" po fakcie).
 *
 * Scope per rola:
 *   - EMPLOYEE: tylko własne (filtr po viewer.id, parametr `employeeId`
 *     ignorowany).
 *   - RECEPTION / ADMIN: wszystkie pracowników, opcjonalny filtr `employeeId`.
 *
 * Sortowanie: `startAt asc` (najstarsze zaległości na górze — żeby nie
 * przeoczyć kart lojalnościowych z dawnymi wizytami).
 */
export const OverdueAppointmentsQuerySchema = z.object({
    employeeId: z.coerce.number().int().positive().optional(),
});
/**
 * GET /appointments?from&to&employeeId&clientId&status
 * - from/to: dni kalendarzowe ("YYYY-MM-DD"), interpretowane jako
 *   [from 00:00, to 23:59:59.999] w strefie procesu. Domyślnie:
 *   ostatnie 7 dni od dziś (bezpieczna wartość, gdyby ktoś zapomniał o filtrze).
 * - status: filtr (lista przez powtórzenie param-u).
 */
export const AppointmentListQuerySchema = z.object({
    from: isoDateOnlySchema.optional(),
    to: isoDateOnlySchema.optional(),
    employeeId: z.coerce.number().int().positive().optional(),
    clientId: z.coerce.number().int().positive().optional(),
    status: AppointmentStatusSchema.optional(),
});
/**
 * Stałe statusy używane przy walidacji listy/PATCH:
 * statusy "terminalne" (CANCELLED/COMPLETED/NO_SHOW) ustawiane są wyłącznie
 * przez dedykowane endpointy (`/cancel`, `/complete`, `/no-show`), żeby
 * pilnować audytu i pól `cancelledAt`/`cancelledReason`.
 */
export const APPOINTMENT_TERMINAL_STATUSES = [
    'CANCELLED',
    'COMPLETED',
    'NO_SHOW',
];
// ---------------------------------------------------------------------
// Faza 8 — Notatki z wizyt + historia klienta
// ---------------------------------------------------------------------
/**
 * Notatka z wizyty. `visibility`:
 *   - EMPLOYEE — widzi tylko autor i ADMIN (notatka prywatna pracownika).
 *   - TEAM     — widzi cały zespół (RECEPTION/EMPLOYEE/ADMIN).
 *
 * Tworzenie i edycja zgodne z matrycą RBAC sekcji 6 CLAUDE.md:
 *   - autor zawsze może edytować/kasować swoje (bez okna czasowego),
 *   - ADMIN może edytować/kasować dowolne,
 *   - EMPLOYEE może dodawać tylko do własnych wizyt (RBAC F7),
 *   - notatki można dopisywać niezależnie od statusu wizyty
 *     (CANCELLED/NO_SHOW również — np. "klientka odwołała w ostatniej chwili").
 */
export const NoteCreateSchema = z.object({
    body: z.string().trim().min(1, 'Treść notatki jest wymagana.').max(5000),
    visibility: NoteVisibilitySchema.optional().default('EMPLOYEE'),
});
export const NoteUpdateSchema = z
    .object({
    body: z.string().trim().min(1, 'Treść notatki jest wymagana.').max(5000).optional(),
    visibility: NoteVisibilitySchema.optional(),
})
    .refine((v) => v.body !== undefined || v.visibility !== undefined, {
    message: 'Brak pól do zaktualizowania.',
});
/**
 * GET /clients/:id/history — wizyty klienta wraz z widocznymi dla viewera
 * notatkami. `search` filtruje po treści notatek (zwracamy wizyty, których
 * przynajmniej jedna widoczna dla viewera notatka pasuje do searcha).
 * Bez `search` zwracamy wszystkie wizyty klienta.
 */
export const ClientHistoryQuerySchema = z.object({
    search: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().min(1).max(10000).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
/**
 * GET /public/employees?serviceIds=1,2 — filtr przez CSV w jednym query param.
 * Brak `serviceIds` = wszyscy aktywni bookable.
 */
export const PublicEmployeesQuerySchema = z.object({
    serviceIds: z
        .string()
        .trim()
        .regex(/^\d+(,\d+)*$/, 'serviceIds musi być listą liczb oddzielonych przecinkiem.')
        .optional(),
});
/**
 * GET /public/availability?serviceIds=1,2&employeeId=3&date=YYYY-MM-DD
 *   - `serviceIds` wymagane (CSV),
 *   - `employeeId` opcjonalny — jeśli pominięty, wybieramy pracownika
 *      automatycznie (pierwszy dostępny wg ID, deterministycznie),
 *   - `date` wymagane.
 *
 * Backend egzekwuje:
 *   - data nie wcześniej niż dzisiaj (lokalnie),
 *   - data nie później niż MAX_BOOKING_HORIZON_DAYS od dziś,
 *   - dla `date == today` slot >= now + MIN_LEAD_TIME_MINUTES.
 */
export const PublicAvailabilityQuerySchema = z.object({
    serviceIds: z
        .string()
        .trim()
        .regex(/^\d+(,\d+)*$/, 'serviceIds musi być listą liczb oddzielonych przecinkiem.'),
    employeeId: z.coerce.number().int().positive().optional(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data musi być w formacie YYYY-MM-DD.'),
});
/**
 * Dane klienta przy rezerwacji publicznej (Faza 9):
 *   - `firstName` i `phone` wymagane (telefon to klucz SMS i unikatowy
 *      identyfikator klienta),
 *   - `lastName` i `email` opcjonalne,
 *   - `rodoConsent` MUSI być `true` (zgoda na przetwarzanie),
 *   - `smsConsent` MUSI być `true` (bez SMS nie wyślemy potwierdzenia),
 *   - `marketingConsent` opcjonalny.
 *
 * Po stronie BE: telefon normalizowany do E.164 (libphonenumber-js).
 * Find-or-create po znormalizowanym telefonie — istniejący klient
 * jest reuse'owany (z aktualizacją imienia, jeśli się zmieniło).
 */
export const PublicBookingClientSchema = z.object({
    firstName: trimmedNonEmpty('Imię', 50),
    lastName: z.string().trim().max(80).nullable().optional(),
    phone: phoneInputSchema,
    email: emailOptionalSchema,
    rodoConsent: z
        .boolean()
        .refine((v) => v === true, 'Wymagana jest zgoda na przetwarzanie danych.'),
    smsConsent: z
        .boolean()
        .refine((v) => v === true, 'Wymagana jest zgoda na komunikację SMS.'),
    marketingConsent: z.boolean().optional().default(false),
});
export const PublicBookingCreateSchema = z.object({
    serviceIds: z.array(z.number().int().positive()).min(1).max(10),
    employeeId: z.number().int().positive(),
    startAt: isoDateTimeSchema,
    client: PublicBookingClientSchema,
});
/** POST /public/bookings/:cancelToken/cancel — bez body. */
/** POST /public/bookings/:cancelToken/reschedule — tylko zmiana terminu. */
export const PublicBookingRescheduleSchema = z.object({
    startAt: isoDateTimeSchema,
});
export const NotificationListQuerySchema = z.object({
    unreadOnly: z.enum(['true', 'false']).optional().default('false'),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
// ---------------------------------------------------------------------
// Faza 13 — Raporty i statystyki (admin)
// ---------------------------------------------------------------------
/**
 * Wspólny zakres dat dla raportów. Oba pola wymagane — UI dostarcza
 * presety (ten miesiąc, ostatnie 7/30 dni, kwartał, custom). Backend
 * interpretuje [from 00:00, to 23:59:59.999] w strefie procesu
 * (Europe/Warsaw), dokładnie tak jak `AppointmentListQuerySchema`.
 *
 * Decyzja Fazy 13: do raportów liczymy WYŁĄCZNIE wizyty COMPLETED,
 * żeby przychód był spójny z przyszłą Fazą 14 (prowizje). NO_SHOW
 * jest brany do osobnego KPI no-show%. CANCELLED i statusy aktywne
 * (BOOKED/CONFIRMED) są pomijane.
 */
export const ReportsRangeQuerySchema = z.object({
    from: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data musi być w formacie YYYY-MM-DD.'),
    to: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data musi być w formacie YYYY-MM-DD.'),
});
/**
 * GET /api/reports/top-services?from&to&limit=3 — ranking usług po
 * przychodzie (suma `AppointmentService.price` dla wizyt COMPLETED).
 */
export const TopServicesQuerySchema = ReportsRangeQuerySchema.extend({
    limit: z.coerce.number().int().min(1).max(50).default(3),
});
/**
 * GET /api/reports/export?from&to&type=appointments — eksport CSV.
 * Format: jeden wiersz nagłówka + lista wizyt z przedziału.
 *
 * Decyzja Fazy 13: `type=appointments` (jedyny wspierany w MVP) eksportuje
 * WSZYSTKIE wizyty z przedziału (COMPLETED, NO_SHOW, CANCELLED) z kolumną
 * `status`, żeby księgowa filtrowała sobie sama w Excelu. Wartości w PLN
 * z kropką dziesiętną, daty ISO w lokalnym `YYYY-MM-DD HH:MM`.
 */
export const ReportsExportQuerySchema = ReportsRangeQuerySchema.extend({
    type: z.enum(['appointments']).optional().default('appointments'),
});
// ---------------------------------------------------------------------
// Faza 14 — Prowizje (commissions)
// ---------------------------------------------------------------------
/**
 * Reguła prowizji per (userId, serviceId|null) z oknem czasowym.
 *
 * Decyzje Fazy 14 (CLAUDE.md sekcja 12 F14 + pytania uzupełniające 2026-05-11):
 *   - `serviceId=null` → stawka BAZOWA (fallback dla każdej usługi pracownika
 *     dla której nie ma reguły per-usługa).
 *   - `percent` ∈ [0..100], maksymalnie 2 miejsca po przecinku. Trzymane
 *     jako string ("30.00") spójnie z polem `price` w `Service`.
 *   - `validFrom` to data początku obowiązywania włącznie (lokalnie, salonowy
 *     dzień). `validTo` (opcjonalne) to data końca obowiązywania włącznie.
 *     Backend zapisuje validFrom=00:00 lokalne dnia X, validTo=23:59:59.999
 *     lokalne dnia Y — porównania z `Appointment.startAt` przez `>=`/`<=`.
 *   - Overlap dla pary (userId, serviceId) blokowany przy POST/PATCH (409).
 *   - Wybór reguły dla pozycji wizyty: najpierw per-usługa, fallback bazowa.
 *   - Brak reguły → pozycja commission=0 + warning (decyzja użytkownika).
 */
const percentInputSchema = z
    .union([z.string(), z.number()])
    .transform((v) => (typeof v === 'number' ? v.toFixed(2) : v.trim()))
    .pipe(z
    .string()
    .regex(/^\d{1,3}(\.\d{1,2})?$/, 'Procent musi być liczbą (np. 30.00).')
    .refine((v) => Number(v) >= 0, 'Procent nie może być ujemny.')
    .refine((v) => Number(v) <= 100, 'Procent nie może przekraczać 100.'));
export const CommissionRuleCreateSchema = z
    .object({
    userId: z.number().int().positive(),
    serviceId: z.number().int().positive().nullable().optional(),
    percent: percentInputSchema,
    validFrom: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data musi być w formacie YYYY-MM-DD.'),
    validTo: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data musi być w formacie YYYY-MM-DD.')
        .nullable()
        .optional(),
})
    .refine((v) => !v.validTo || v.validTo >= v.validFrom, {
    message: 'Data końcowa musi być >= początkowej.',
    path: ['validTo'],
});
/**
 * PATCH — pozwalamy edytować `percent`, `validFrom`, `validTo`. `userId`
 * i `serviceId` są kluczem reguły; ich zmiana = skasuj i utwórz nową.
 * Walidacja overlapu działa też w trybie PATCH (z wykluczeniem id-edytowanego).
 */
export const CommissionRuleUpdateSchema = z
    .object({
    percent: percentInputSchema.optional(),
    validFrom: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data musi być w formacie YYYY-MM-DD.')
        .optional(),
    validTo: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data musi być w formacie YYYY-MM-DD.')
        .nullable()
        .optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Brak pól do zaktualizowania.' });
export const CommissionRuleListQuerySchema = z.object({
    userId: z.coerce.number().int().positive().optional(),
    serviceId: z.coerce.number().int().positive().optional(),
    /** `active=true` — tylko obecnie obowiązujące (validFrom<=today<=validTo|∞). */
    active: z.enum(['true', 'false', 'all']).optional().default('all'),
});
/**
 * GET /api/commissions/calculate?userId&month=YYYY-MM
 *   - `userId` wymagane (admin: dowolny; non-admin: tylko własne — backend
 *     zwróci 403 jeśli inne).
 *   - `month` w formacie "YYYY-MM" (przykład: "2026-03").
 *   - Backend interpretuje przedział jako [pierwszy dzień 00:00 lokalnie,
 *     pierwszy dzień następnego miesiąca 00:00 lokalnie) — wizyty z
 *     `startAt` w tym przedziale, status COMPLETED.
 */
export const CommissionCalculateQuerySchema = z.object({
    userId: z.coerce.number().int().positive(),
    month: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Miesiąc musi być w formacie YYYY-MM.'),
});
/** GET /api/commissions/export — CSV z liniami prowizyjnymi za miesiąc. */
export const CommissionExportQuerySchema = CommissionCalculateQuerySchema;
// ---------------------------------------------------------------------
// Faza 15 — Karty lojalnościowe (system tierowy)
// ---------------------------------------------------------------------
/**
 * Tier ∈ 0 (brak) | 1 (stały klient) | 2 (VIP). Wartości stałe w czasie —
 * raz dopisane do schematu DTO, używane przez backend (snapshot na wizycie)
 * i frontend (badge / tekstowy label).
 */
export const LoyaltyTierSchema = z.union([z.literal(0), z.literal(1), z.literal(2)]);
/**
 * Wyliczenie efektywnej zniżki dla klienta:
 *   - `automaticTier` / `automaticPercent` — wynikają z liczby wizyt
 *   - `manualPercent` — bonus admina (override floor)
 *   - `effectivePercent = max(automaticPercent, manualPercent)`
 *
 * Helper jest pure i nie czyta .env — config wstrzykiwany. Backend trzyma
 * źródło prawdy w env, ale używa tej samej funkcji, żeby UI i serwer
 * liczyły identycznie (UI dostaje `effectivePercent` z backendu, ale chce
 * też wiedzieć "ile do następnego tieru" lokalnie).
 */
export function computeLoyaltyDiscount(visitsCount, manualDiscountPercent, config) {
    const v = Math.max(0, Math.floor(visitsCount));
    const m = Math.max(0, Math.min(100, Math.floor(manualDiscountPercent)));
    let automaticTier = 0;
    let automaticPercent = 0;
    // Wyższy próg wygrywa (decyzja: tiery się wymieniają, nie kumulują).
    if (v >= config.tier2.visits) {
        automaticTier = 2;
        automaticPercent = config.tier2.percent;
    }
    else if (v >= config.tier1.visits) {
        automaticTier = 1;
        automaticPercent = config.tier1.percent;
    }
    const effectivePercent = Math.max(automaticPercent, m);
    return {
        automaticTier,
        automaticPercent,
        manualPercent: m,
        effectivePercent,
    };
}
/**
 * PATCH /api/loyalty/cards/:clientId/manual-discount — admin nadaje/zmienia
 * bonus. Wartość 0 czyści override (klient wraca na automatyczny tier).
 * 100 to teoretyczne maksimum (klient płaci 0) — sensowne wartości to 10/20,
 * ale nie blokujemy wyższych z góry (np. promocja "darmowa pierwsza wizyta").
 */
export const LoyaltyManualDiscountUpdateSchema = z.object({
    manualDiscountPercent: z
        .number()
        .int('Procent musi być liczbą całkowitą.')
        .min(0, 'Procent nie może być ujemny.')
        .max(100, 'Procent nie może przekraczać 100.'),
});
// ---------------------------------------------------------------------
// Faza 16 — Galeria portfolio pracowników
// ---------------------------------------------------------------------
/**
 * Plik uploadu obsługujemy przez multer (multipart/form-data). Pola
 * `caption` i `serviceId` przychodzą w tej samej formie jako stringi.
 * Ten schemat waliduje payload po przetłumaczeniu (`req.body` po multerze).
 *
 * `serviceId` opcjonalny — pracownik może oznaczyć "to zdjęcie to
 * koloryzacja" lub zostawić puste (ogólne portfolio). Portal publiczny
 * filtruje galerię po wybranych usługach (Faza 16, decyzja użytkownika).
 *
 * Limit `caption` — 200 znaków, krótko (alt-text + ewentualny tytuł).
 */
export const GalleryItemCreateSchema = z.object({
    caption: z
        .string()
        .trim()
        .max(200, 'Opis może mieć najwyżej 200 znaków.')
        .nullable()
        .optional(),
    serviceId: z
        .union([z.string(), z.number()])
        .transform((v) => (typeof v === 'string' ? v.trim() : v))
        .transform((v) => {
        if (v === '' || v === null || v === undefined)
            return null;
        const n = typeof v === 'number' ? v : Number(v);
        return Number.isFinite(n) ? n : NaN;
    })
        .refine((v) => v === null || (Number.isInteger(v) && v > 0), {
        message: 'serviceId musi być dodatnią liczbą całkowitą lub puste.',
    })
        .nullable()
        .optional(),
});
export const GalleryItemUpdateSchema = z
    .object({
    caption: z
        .string()
        .trim()
        .max(200, 'Opis może mieć najwyżej 200 znaków.')
        .nullable()
        .optional(),
    serviceId: z.number().int().positive().nullable().optional(),
    order: z.number().int().min(0).max(10_000).optional(),
})
    .refine((v) => v.caption !== undefined || v.serviceId !== undefined || v.order !== undefined, { message: 'Brak pól do zaktualizowania.' });
export const GalleryListQuerySchema = z.object({
    userId: z.coerce.number().int().positive().optional(),
    serviceId: z.coerce.number().int().positive().optional(),
});
/** Limit liczby zdjęć per pracownik (decyzja Fazy 16, 20). */
export const GALLERY_LIMIT_PER_USER = 20;
// ---------------------------------------------------------------------
// Faza 18 — Główna notatka klienta (jedna na klienta, widoczna przy
// każdej obsłudze klienta: modal wizyty, kafelek w kalendarzu, lista
// klientów, karta klienta).
// ---------------------------------------------------------------------
/**
 * Maks. długość treści głównej notatki klienta. Większa niż w notatkach
 * wizyt (Note: 5000) bo ta jest "katalogowa" — preferencje, alergie,
 * historie, kontekst — i siedzi tam długo. 4000 znaków to ok. 2-3 ekrany
 * tekstu, wystarczająco.
 */
export const CLIENT_MAIN_NOTE_MAX_LENGTH = 4000;
/**
 * Długość fragmentu pokazywanego w listach (`ClientListItem.mainNote`,
 * `AppointmentClientSummary.mainNote`, tooltipy w kalendarzu). Backend
 * zwraca podgląd bez dopisanego "…" — UI sam dorzuca jeśli
 * `bodyPreview.length < fullBody.length`.
 */
export const CLIENT_MAIN_NOTE_PREVIEW_LENGTH = 120;
/**
 * PUT /api/clients/:id/main-note — upsert (jednocześnie create i update).
 * `visibility` opcjonalne — przy create default TEAM (decyzja Fazy 18:
 * standardowy use-case to "wszyscy obsługujący widzą"); przy update brak
 * pola nie zmienia widoczności.
 *
 * `body` pusty po trim → odrzucamy (do zerowania używaj DELETE).
 */
export const ClientMainNoteUpsertSchema = z.object({
    body: z
        .string()
        .trim()
        .min(1, 'Treść notatki jest wymagana.')
        .max(CLIENT_MAIN_NOTE_MAX_LENGTH, `Notatka może mieć najwyżej ${CLIENT_MAIN_NOTE_MAX_LENGTH} znaków.`),
    visibility: NoteVisibilitySchema.optional(),
});
//# sourceMappingURL=index.js.map